/**
 * @file ActionModalStyleA.js
 * @description A specific style of action modal that includes title, subtitle, text, and action buttons.
 */

import style from "./modals.module.scss";
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { getText } from '../../../i18n';

/**
 * ActionModalA component, rendering a styled modal for confirmations.
 * @param {ModalConfig} modalProps - The properties for configuring the modal.
 * @returns {React.ReactElement} The rendered component.
 */
const ActionModalA = ({
  title,
  subtitle,
  text,
  onYes,
  onNo,
  onHide,
  yesButtonText,
  noButtonText,
}) => {
  const footerRef = useRef(null);
  useEffect(() => {
    if (footerRef.current) {
      const buttons = footerRef.current.querySelectorAll(".btn");
      let maxWidth = 0;

      buttons.forEach((button) => {
        maxWidth = Math.max(maxWidth, button.offsetWidth);
      });

      buttons.forEach((button) => {
        button.style.width = `${maxWidth}px`;
      });
    }
  }, []);

  const handleYes = () => {
    onYes();
    onHide();
  };

  const handleNo = () => {
    onNo();
    onHide();
  };

  return (
    <div className={`${style.container} modal-backdrop`}>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
        aria-modal="true"
        role="dialog"
      >
        <div
          className={`modal-dialog modal-lg modal-dialog-centered ${style.modalSizing}`}
        >
          <div className={`modal-header ${style.modalHeader}`}></div>
          <div className={`modal-content ${style.modalContent}`}>
            <button
              type="button"
              className={`btn-close ${style.dismissBtn}`}
              onClick={onHide}
              aria-label={getText('msg_bbfa773e5a63')}
            />
            <div className={`modal-body text-center ${style.modalBody}`}>
              <div className={`col-12 ${style.SVGIconContainer}`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M7.005 3.1a1 1 0 1 1 1.99 0l-.388 6.35a.61.61 0 0 1-1.214 0zM7 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0"
                    fill="#FFA800"
                  />
                </svg>
              </div>
              {title && (
                <h5 className={`modal-title ${style.title}`}>{title}</h5>
              )}
              {subtitle && <h6 className={`${style.subtitle}`}>{subtitle}</h6>}
              {text && (
                <p
                  className={`${style.textContainer}`}
                  dangerouslySetInnerHTML={{ __html: text }}
                ></p>
              )}

              <div
                className={`modal-footer ${style.modalFooter}`}
                ref={footerRef}
              >
                <button
                  type="button"
                  className="btn ing-warning-btn-outline"
                  onClick={handleNo}
                >
                  {noButtonText}
                </button>
                <button
                  type="button"
                  className="btn ing-warning-btn"
                  onClick={handleYes}
                >
                  {yesButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ActionModalA.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  text: PropTypes.string,
  onYes: PropTypes.func.isRequired,
  onNo: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ActionModalA;
